import React, { useState } from 'react';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { Box, Button, Grid, Stack, TextField, CircularProgress, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { bulkUpload, getFeeForecastData } from '../../../redux/features/feeForecastSlice';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import { Icon } from '@iconify/react';

// One labeled count in the results popup's summary row.
const ResultStat = ({ label, value, color }) => (
  <Grid item xs={4}>
    <Box sx={{ textAlign: 'center' }}>
      <Typography variant="h5" fontWeight="700" color={color || 'text.primary'}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  </Grid>
);

const BulkUpload = ({ dispatchParams }) => {
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(null);
  const [file, setFile] = useState(null);
  // Backend already returns { total, processed, skipped, sample } - this just displays it.
  const [resultsOpen, setResultsOpen] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const handleApiResponse = useHandleApiResponse();

  const { manualForecast, loading } = useSelector((state) => state.feeForecast);

  /* ---------------- CSV DOWNLOAD ---------------- */

  const downloadCSV = () => {
    if (!manualForecast || manualForecast.length === 0) return;

    const headers = Object.keys(manualForecast[0]);

    const csvRows = [
      headers.join(','),
      ...manualForecast.map((row) => headers.map((field) => `"${row[field] ?? ''}"`).join(',')),
    ];

    const csvString = csvRows.join('\n');

    const blob = new Blob([csvString], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'manual_forecast_template.csv';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /* ---------------- CSV PARSE ---------------- */

  const parseCSV = (text) => {
    const rows = text.split('\n').map((row) => row.split(','));
    const headers = rows[0];

    const data = rows.slice(1).map((row) => {
      let obj = {};

      headers.forEach((header, index) => {
        obj[header.replace(/"/g, '')] = row[index]?.replace(/"/g, '') || '';
      });

      return obj;
    });

    return data.filter((item) => Object.values(item).some((v) => v !== ''));
  };

  /* ---------------- CSV UPLOAD ---------------- */

  const handleUpload = () => {
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      const text = e.target.result;

      const entries = parseCSV(text);

      dispatch(
        bulkUpload({
          date: date ? dayjs(date).format('YYYY-MM-DD') : '',
          entries,
        }),
      ).then((res) => {
        handleApiResponse(res, {
          onSuccess: () => {
            setOpen(false);
            dispatch(getFeeForecastData(dispatchParams));

            setFile(null);
            setDate(null);
            setUploadResults(res.payload.data || null);
            setResultsOpen(true);
          },
          onFailed: () => {
            console.log('Something went wrong');
          },
        });
      });
    };

    reader.readAsText(file);
  };

  return (
    <>
    <GlobalDialog
      title="Bulk Upload — Manual Forecast Fees"
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      popupButton={
        <Button size="small" variant="contained" onClick={() => setOpen(true)}>
          Bulk Upload — Manual Forecast Fees
        </Button>
      }
      content={
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            For students on a Manual forecast (no course package/option selected) only. Download the current data CSV, set each student's amount to collect and expected sessions, and upload the updated CSV below. For all other students, use "Bulk Upload — Expected Sessions" instead.
          </Typography>
          {/* DOWNLOAD CSV */}
          <Button
            startIcon={<Icon icon="line-md:download-loop" width="24" height="24" />}
            variant="contained"
            onClick={downloadCSV}
          >
            Download CSV for Editing
          </Button>

          {/* DATE PICKER */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Select Date"
              value={date}
              onChange={(newValue) => setDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>

          {/* FILE SELECT */}
          <Button
            startIcon={<Icon icon="material-symbols:upload" width="24" height="24" />}
            variant="outlined"
            component="label"
          >
            Select CSV File
            <input
              hidden
              type="file"
              accept=".csv"
              onChange={(e) => {
                setFile(e.target.files[0]);
              }}
            />
          </Button>

          {file && (
            <Typography variant="body2">
              Selected file: <b>{file.name}</b>
            </Typography>
          )}

          {/* UPLOAD BUTTON */}
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={loading || !file}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Uploading...' : 'Upload CSV'}
          </Button>

        </Stack>
      }
    />
    <GlobalDialog
      title="Bulk Upload Results"
      open={resultsOpen}
      setOpen={setResultsOpen}
      fullWidth
      maxWidth="sm"
      closeButton
      popupButton={null}
      content={
        <Stack spacing={2}>
          <Grid container spacing={1}>
            <ResultStat label="Total Rows" value={uploadResults?.total ?? 0} />
            {/* Backend's "processed" counts every row it looped over, changed or not - it always
                equals "total". The count of rows actually written is total - skipped. */}
            <ResultStat
              label="Updated"
              value={(uploadResults?.total ?? 0) - (uploadResults?.skipped ?? 0)}
              color="success.main"
            />
            <ResultStat label="Skipped (No Change)" value={uploadResults?.skipped ?? 0} color="text.secondary" />
          </Grid>

          {uploadResults?.sample?.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Sample of saved rows, to confirm your numbers actually landed:
              </Typography>
              <Box sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                {uploadResults.sample.map((row, idx) => (
                  <div key={idx}>
                    • {row.studentName || `Enrollment ${row.courseEnrollmentId}`}
                    {row.admissionNo ? ` (${row.admissionNo})` : ''}
                    {row.courseName ? ` — ${row.courseName}` : ''} → ₹{row.amountToCollect} to collect, {row.expectedSessions} sessions
                  </div>
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      }
      actionButton={<Button onClick={() => setResultsOpen(false)}>Close</Button>}
    />
    </>
  );
};

export default BulkUpload;
